import {useAutoAnimate} from '@formkit/auto-animate/react';
import cx from 'classnames';
import {memo, useCallback, useEffect, useRef, useState} from 'react';
import Skeleton from 'react-loading-skeleton';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {getPydanticError} from '~/common/utils/errors';
import {parseEventDateTime} from '~/common/utils/time';
import {AvatarImg} from '~/ViewInvitation/components/Guestlist/Avatar';
import {useCurrentGuestId} from '~/ViewInvitation/components/Guestlist/hooks/useCurrentGuestId';
import {usePostContext} from '~/ViewInvitation/components/Guestlist/hooks/usePostContext';
import {DeleteReplyConfirmModal} from '~/ViewInvitation/components/Guestlist/modals/DeleteConfirmModals';
import {Likes} from '~/ViewInvitation/components/Guestlist/Post/Likes';
import {ReplyForm} from '~/ViewInvitation/components/Guestlist/Post/ReplyForm';
import type {ReplyResponse} from '~/ViewInvitation/types/api';
import {Reply as ReplyType} from '~/ViewInvitation/types/api';
import type {Guest} from '~/ViewInvitation/types/guests';
import {Button} from '~library/atoms/Button';
import {toastAlert} from '~library/atoms/Toast';
import {Typography} from '~library/atoms/Typography';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';

import * as styles from './Reply.module.scss';

interface ReplyProps {
  guest: Guest;
  reply: ReplyType;
}

export const Reply = memo(({guest, reply}: ReplyProps) => {
  const [commentAnimationRef] = useAutoAnimate();
  const ref = useRef<HTMLDivElement>(null);
  const isLargishAndUp = useIsLargishAndUp();
  const {userId} = useCurrentGuestId();
  const {isEditing: isPostEditing, setIsEditing: setIsPostEditing} = usePostContext();
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteModalShowing, setIsDeleteModalShowing] = useState(false);
  const datetime = parseEventDateTime(reply.created, 'utc');
  const timestamp = datetime.isValid ? datetime.setZone('local').toRelative() : '';

  const resetEditingState = useCallback(() => {
    setIsPostEditing(false);
    setIsEditing(false);
  }, [setIsPostEditing]);

  useEffect(
    () => () => {
      resetEditingState();
    },
    [resetEditingState]
  );

  useEffect(() => {
    if (isEditing) {
      ref.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
  }, [isEditing]);

  const onShowEdit = useCallback(() => {
    setIsPostEditing(true);
    setIsEditing(true);
  }, [setIsPostEditing]);

  const onEdit = useCallback(
    (response: ReplyResponse | Error) => {
      if (response instanceof Error) {
        evite.error('Failed to edit comment', response);
        const message = getPydanticError(response, '');
        toastAlert(`Unable to edit comment.\n${message}`, {
          icon: <AlertLineIcon />,
          type: 'error',
        });
      } else {
        resetEditingState();
      }
    },
    [resetEditingState]
  );

  const onDelete = useCallback((e?: Error) => {
    if (!e) {
      setIsDeleteModalShowing(false);
    }
  }, []);

  const showDeleteModal = useCallback(() => setIsDeleteModalShowing(true), []);
  const hideDeleteModal = useCallback(() => setIsDeleteModalShowing(false), []);

  const showComment = !isLargishAndUp || (isLargishAndUp && !isEditing);
  const ownsComment = userId === reply.userId;

  return (
    <div
      key={reply.id}
      ref={ref}
      className={cx(styles.reply, {
        [styles.replyActive]: isEditing,
      })}
    >
      <AvatarImg guest={guest!} size={24} />
      <div className={styles.container}>
        <div className={styles.contents} ref={commentAnimationRef}>
          <Typography variant="list1" semibold>
            {reply?.creator?.name}
          </Typography>
          {isEditing && (
            <ReplyForm
              commentText={reply?.comment}
              onReply={onEdit}
              post={reply}
              closeEditor={resetEditingState}
            />
          )}
          {showComment && (
            <Typography variant="paragraph3">{reply?.comment || <Skeleton />}</Typography>
          )}
          <div className={styles.actions}>
            <Typography variant="paragraph4" color="pebble">
              {timestamp}
            </Typography>
            {ownsComment && (
              <span className={styles.actionsButtons}>
                <Button
                  className={styles.editButton}
                  data-qa-id={`edit-post-button-${reply?.creator?.id}-${reply?.id}`}
                  variant="link"
                  onClick={isEditing ? resetEditingState : onShowEdit}
                  disabled={!isEditing && isPostEditing}
                  title={
                    !isEditing && isPostEditing
                      ? 'Please finish replying to or editing the other comment'
                      : ''
                  }
                >
                  {isEditing ? 'Cancel' : 'Edit'}
                </Button>
                <Typography variant="paragraph4" color="silver" size="large">
                  |
                </Typography>
                <Button
                  data-qa-id={`edit-post-button-${reply?.creator?.id}-${reply?.id}`}
                  variant="link"
                  onClick={showDeleteModal}
                >
                  Delete
                </Button>
              </span>
            )}
          </div>
        </div>
        <Likes likes={reply?.likes} likedBy={reply?.likedBy} postId={reply?.id} />
        <DeleteReplyConfirmModal
          isOpen={isDeleteModalShowing}
          postId={reply?.id}
          onCancel={hideDeleteModal}
          onDelete={onDelete}
        />
      </div>
    </div>
  );
});

Reply.displayName = 'Reply';
